
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function RentalGuide() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'ja' | 'en' | 'zh';

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Page Header */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('rental_guide_title')}
            </h1>
            <p className="text-xl text-gray-600">
              {t('rental_guide_subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative h-96 rounded-xl overflow-hidden mb-12">
            <img
              src="https://public.readdy.ai/ai/img_res/b541dd401508aa111c7caf822ad598cc.jpg"
              alt="Tokyo Rental Guide"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
              <div className="text-center text-white">
                <h2 className="text-3xl font-bold mb-4">外国人の方も安心してお住まいいただけます</h2>
                <p className="text-xl">私たちが全面的にサポートいたします</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Japanese Rental System Overview */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">日本の賃貸システムについて</h2>
            
            <div className="bg-blue-50 p-8 rounded-xl mb-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-blue-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-blue-900 mb-3">外国人の方へ重要なお知らせ</h3>
                  <p className="text-blue-800 leading-relaxed">
                    日本では、外国籍の方がアパートを借りる際に、日本人の保証人または保証会社の利用が一般的に必要とされています。
                    これは法的な要件であり、多くの大家さんや不動産会社が求める条件です。
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-lg border">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-user-line text-red-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">保証人が必要な理由</h3>
                <ul className="text-gray-700 space-y-2 text-sm">
                  <li className="flex items-start space-x-2">
                    <i className="ri-check-line text-green-600 mt-1"></i>
                    <span>家賃滞納時の支払い保証</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <i className="ri-check-line text-green-600 mt-1"></i>
                    <span>退去時の原状回復費用保証</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <i className="ri-check-line text-green-600 mt-1"></i>
                    <span>緊急時の連絡先確保</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <i className="ri-check-line text-green-600 mt-1"></i>
                    <span>文化的・言語的サポート</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-building-line text-orange-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">保証会社という選択肢</h3>
                <ul className="text-gray-700 space-y-2 text-sm">
                  <li className="flex items-start space-x-2">
                    <i className="ri-check-line text-green-600 mt-1"></i>
                    <span>個人保証人が不要</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <i className="ri-check-line text-green-600 mt-1"></i>
                    <span>月額保証料で利用可能</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <i className="ri-check-line text-green-600 mt-1"></i>
                    <span>24時間サポート体制</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <i className="ri-check-line text-green-600 mt-1"></i>
                    <span>多言語対応サービス</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Our Solution */}
          <section className="py-16">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">私たちの解決策</h2>
              
              <div className="bg-blue-50 p-8 rounded-xl mb-12">
                <div className="flex items-center justify-center mb-6">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                    <i className="ri-money-dollar-circle-line text-white text-2xl"></i>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-blue-600 mb-4">前金制度による簡単入居</h3>
                <p className="text-lg text-gray-700 mb-6">
                  従来の複雑な審査プロセスを簡素化。<strong>前金2ヶ月以上をお支払いいただくことで、どなたでも入居可能</strong>です。
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                  <div className="bg-white p-6 rounded-lg shadow-sm">
                    <i className="ri-user-forbid-line text-red-500 text-3xl mb-3"></i>
                    <h4 className="font-semibold text-gray-900 mb-2">審査なし</h4>
                    <p className="text-sm text-gray-600">複雑な書類審査は不要</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-sm">
                    <i className="ri-user-unfollow-line text-red-500 text-3xl mb-3"></i>
                    <h4 className="font-semibold text-gray-900 mb-2">保証人不要</h4>
                    <p className="text-sm text-gray-600">日本人の保証人は必要ありません</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-sm">
                    <i className="ri-building-line text-red-500 text-3xl mb-3"></i>
                    <h4 className="font-semibold text-gray-900 mb-2">保証会社不要</h4>
                    <p className="text-sm text-gray-600">保証会社との契約も不要</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-8 rounded-xl">
                <h3 className="text-2xl font-bold text-green-600 mb-4">柔軟な契約期間と価格設定</h3>
                <p className="text-lg text-gray-700 mb-6">
                  6ヶ月・12ヶ月の長期契約では、価格についても柔軟にご相談に応じます。
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-lg shadow-sm">
                    <i className="ri-calendar-check-line text-green-600 text-2xl mb-3"></i>
                    <h4 className="font-semibold text-gray-900 mb-2">6ヶ月契約</h4>
                    <p className="text-sm text-gray-600">中期滞在に最適な契約期間</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-sm">
                    <i className="ri-calendar-2-line text-green-600 text-2xl mb-3"></i>
                    <h4 className="font-semibold text-gray-900 mb-2">12ヶ月契約</h4>
                    <p className="text-sm text-gray-600">長期滞在でさらにお得に</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Flexible Pricing */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">柔軟な価格設定</h2>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-calendar-line text-green-600 text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-semibold mb-4">長期契約割引</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium">6ヶ月契約</span>
                      <span className="text-green-600 font-semibold">5-10% 割引</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium">12ヶ月契約</span>
                      <span className="text-green-600 font-semibold">10-15% 割引</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-discuss-line text-purple-600 text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-semibold mb-4">個別相談対応</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start space-x-2">
                      <i className="ri-check-line text-green-600 mt-1"></i>
                      <span>ご予算に応じた価格調整</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <i className="ri-check-line text-green-600 mt-1"></i>
                      <span>契約期間による柔軟な設定</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <i className="ri-check-line text-green-600 mt-1"></i>
                      <span>企業契約での特別価格</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <i className="ri-check-line text-green-600 mt-1"></i>
                      <span>季節による価格調整</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Process Steps */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">ご入居までの流れ</h2>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2">お問い合わせ・ご相談</h3>
                  <p className="text-gray-600">ご希望の条件をお聞かせください。最適なお部屋をご提案いたします。</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2">お部屋見学・価格相談</h3>
                  <p className="text-gray-600">実際のお部屋をご覧いただき、契約期間に応じた価格をご相談いたします。</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  3
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2">デポジット支払い</h3>
                  <p className="text-gray-600">保証人の代わりにデポジットをお支払いいただきます。</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  4
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2">契約手続き・ご入居</h3>
                  <p className="text-gray-600">簡単な手続きでご契約完了。すぐにご入居いただけます。</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gray-50 p-8 rounded-xl text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">まずはお気軽にご相談ください</h2>
            <p className="text-gray-600 mb-6">
              日本での賃貸に関するご不安やご質問、価格についてのご相談など、何でもお聞かせください。
              経験豊富なスタッフが丁寧にサポートいたします。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-8 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                お問い合わせ
              </a>
              <a
                href="/rooms"
                className="bg-white hover:bg-gray-50 text-blue-600 border-2 border-blue-600 py-3 px-8 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                お部屋を見る
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
